/**
 * SourceSearch - A reusable component for searching and selecting news sources.
 *
 * Usage:
 *   new SourceSearch({
 *     container: '.source-search',      // Container element selector or jQuery object
 *     hiddenInput: 'input[name=source]', // Hidden input for source ID (optional, defaults to input[name=source] inside container)
 *     onSelect: function(id, name) {},   // Callback when source is selected
 *     onClear: function() {}             // Callback when source is cleared
 *   });
 *
 * Expected HTML structure inside container:
 *   <input type="hidden" name="source">
 *   <input type="text" class="source-input" placeholder="Search sources...">
 *   <div class="source-input-clear tw-hidden">...</div>
 *   <div class="source-dropdown" style="display: none"></div>
 */
class SourceSearch {
  constructor(options = {}) {
    this.$container = $(options.container || '.source-search');
    if (!this.$container.length) return;

    this.$hidden = options.hiddenInput
      ? $(options.hiddenInput)
      : this.$container.find('input[type=hidden]');
    this.$input = this.$container.find('.source-input');
    this.$dropdown = this.$container.find('.source-dropdown');
    this.$clearBtn = this.$container.find('.source-input-clear');

    this.onSelect = options.onSelect || function() {};
    this.onClear = options.onClear || function() {};
    this.searchDelay = options.searchDelay || 300;
    this.searchUrl = options.searchUrl || '/a/monitor/source';

    this._searchRequest = null;
    this._searchTimer = null;

    this._bindEvents();
  }

  _bindEvents() {
    const self = this;

    // Input typing - trigger search
    this.$input.on('input', function() {
      const term = $(this).val().trim();
      clearTimeout(self._searchTimer);

      // Clear hidden value when user types (searching for new source)
      self.$hidden.val('');

      if (term.length > 0) {
        self.$clearBtn.removeClass('tw-hidden');
      } else {
        self.$clearBtn.addClass('tw-hidden');
        self.$dropdown.hide();
        return;
      }

      if (term.length >= 2) {
        self._searchTimer = setTimeout(() => self._search(term), self.searchDelay);
      }
    });

    // Focus - show dropdown if has content
    this.$input.on('focus', function() {
      if ($(this).val().trim().length >= 2 && !self.$hidden.val()) {
        self.$dropdown.show();
      }
    });

    // Clear button
    this.$clearBtn.on('click', function() {
      self.clear();
      self.$input.focus();
    });

    // Click outside - hide dropdown
    $(document).on('click', function(e) {
      if (!$(e.target).closest(self.$container).length) {
        self.$dropdown.hide();
      }
    });
  }

  _search(term) {
    const self = this;
    this._searchRequest?.abort();
    this.$clearBtn.find('iconify-icon').attr('icon', 'svg-spinners:90-ring-with-bg');

    this._searchRequest = $.get(this.searchUrl, { term }, function(results) {
      self.$dropdown.empty();

      if (results.length > 0) {
        self.$dropdown.append('<div class="tw-text-tertiary tw-text-xs tw-px-4 tw-pt-3 tw-pb-1">Sources</div>');
        results.forEach(source => {
          const $item = $(`
            <div class="source-item tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-cursor-pointer hover:tw-bg-grey tw-border-b tw-border-border last:tw-border-b-0" data-id="${source.id}" data-name="${source.name}">
              <iconify-icon icon="iconoir:page" class="tw-text-lg tw-text-brand-600"></iconify-icon>
              <div>
                ${source.name}
                <div class="tw-text-xs tw-text-tertiary">${source.id}</div>
              </div>
            </div>
          `);
          $item.on('click', function() {
            self.select($(this).data('id'), $(this).data('name'));
          });
          self.$dropdown.append($item);
        });
        self.$dropdown.show();
      } else {
        self.$dropdown.append(`
          <div class="tw-text-center tw-p-4 tw-text-tertiary">
            No sources found for "${term}"
          </div>
        `);
        self.$dropdown.show();
      }
    })
    .always(function() {
      self.$clearBtn.find('iconify-icon').attr('icon', 'basil:cross-outline');
      self._searchRequest = null;
    });
  }

  select(id, name) {
    this.$hidden.val(id);
    this.$input.val(name);
    this.$dropdown.hide();
    this.$clearBtn.removeClass('tw-hidden');
    this.onSelect(id, name);
  }

  clear() {
    this.$hidden.val('');
    this.$input.val('');
    this.$clearBtn.addClass('tw-hidden');
    this.$dropdown.hide();
    this.onClear();
  }

  // Get current value
  getValue() {
    return {
      id: this.$hidden.val(),
      name: this.$input.val()
    };
  }

  // Set value programmatically
  setValue(id, name) {
    this.$hidden.val(id || '');
    this.$input.val(name || '');
    if (id) {
      this.$clearBtn.removeClass('tw-hidden');
    } else {
      this.$clearBtn.addClass('tw-hidden');
    }
  }
}

// Expose so cross-file consumers (pages/monitor/monitor.js) and esbuild
// bundles see the same SourceSearch.
window.SourceSearch = SourceSearch;
